import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Label } from '@shared/components/ui/label';
import { RadioOption, RadioSelect } from '@shared/components/ui/RadioSelect';
import { apiClientWH } from '@shared/services';
import { cn } from '@shared/utils/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type EntityType = 'individual' | 'self_employed' | 'legal_entity';
export type ResidencyType = 'spain' | 'eu' | 'outside_eu';
export type VATResult = {
  percentage: number;
  subject: boolean;
  description: string;
  isIntraCommunity?: boolean;
};

interface VATCalculatorProps {
  value: string | null;
  onChange: (value: string) => void;
  country?: string;
  region?: string;
  vat?: string; // CIF/NIF del cliente para verificar en VIES
}

interface VIESResponse {
  valid: boolean;
  country_code: string;
  vat_number: string;
  name?: string;
  address?: string;
  request_date?: string;
  error?: string;
}

export const VATCalculator = ({ onChange, region, vat, country }: VATCalculatorProps) => {
  const { t } = useTranslation('createEditClientsModule');
  const [entityType, setEntityType] = useState<EntityType | ''>('');
  const [residencyType, setResidencyType] = useState<ResidencyType | ''>('');
  const [isCanaryIslands, setIsCanaryIslands] = useState<boolean | null>(null);
  const [hasVATRegistration, setHasVATRegistration] = useState<boolean | null>(null);
  const [vatResult, setVatResult] = useState<VATResult | null>(null);
  const [isCheckingVIES, setIsCheckingVIES] = useState(false);
  const [viesResult, setViesResult] = useState<VIESResponse | null>(null);
  const [viesError, setViesError] = useState<string | null>(null);
  const lastCalculatedValue = useRef<string | null>(null);

  // Función para convertir el resultado del cálculo a código de IVA
  const getTaxCode = (result: VATResult): string => {
    if (result.isIntraCommunity) {
      return 's_iva_intras';
    }
    if (result.percentage === 21) {
      return 's_iva_21';
    }
    if (result.percentage === 0) {
      return 's_iva_nosujeto';
    }
    // Por defecto, si no coincide con ningún caso
    return 's_iva_21';
  };

  // Función para verificar VAT en VIES
  const checkVIES = useCallback(async () => {
    if (!vat || !country) {
      setViesError(t('vatCalculator.vies.error.missingData'));
      return;
    }

    // Solo verificar si es un país de la UE
    const euCountries = [
      'AT',
      'BE',
      'BG',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GR',
      'HR',
      'HU',
      'IE',
      'IT',
      'LT',
      'LU',
      'LV',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
    ];
    const countryCode = country.toUpperCase();
    if (!euCountries.includes(countryCode)) {
      setViesError(t('vatCalculator.vies.error.notEUCountry'));
      return;
    }

    setIsCheckingVIES(true);
    setViesError(null);
    setViesResult(null);

    try {
      // Limpiar el VAT number (remover espacios, guiones, etc.)
      let cleanVat = vat.replace(/[\s-]/g, '').toUpperCase();

      // Si el VAT number ya incluye el código de país al inicio, removerlo
      if (cleanVat.startsWith(countryCode)) {
        cleanVat = cleanVat.substring(countryCode.length);
      }

      // Llamar al endpoint del backend para verificar VAT en VIES
      const response = await apiClientWH.get<VIESResponse>(`/vat/validate/${countryCode}/${cleanVat}`);

      const data = response.data;

      if (data.valid) {
        setViesResult({
          valid: true,
          country_code: data.country_code,
          vat_number: data.vat_number,
          name: data.name,
          address: data.address,
        });
        setHasVATRegistration(true);
      } else {
        // Si hay un error específico en la respuesta, mostrarlo
        const errorMessage = data.error || t('vatCalculator.vies.invalid');
        setViesResult({
          valid: false,
          country_code: data.country_code,
          vat_number: data.vat_number,
        });
        setViesError(errorMessage);
        setHasVATRegistration(false);
      }
    } catch (error: any) {
      console.error('Error verificando VIES:', error);
      // Manejar errores de la API
      const errorMessage = error?.response?.data?.error || error?.message || t('vatCalculator.vies.error.checkFailed');
      setViesError(errorMessage);
    } finally {
      setIsCheckingVIES(false);
    }
  }, [vat, country, t]);

  // Detectar si está en Canarias basado en región
  useEffect(() => {
    if (region) {
      const canaryRegions = ['Las Palmas', 'Santa Cruz de Tenerife', 'Canarias'];
      const isCanary = canaryRegions.some((r) => region.toLowerCase().includes(r.toLowerCase()));
      setIsCanaryIslands(isCanary);
    } else {
      setIsCanaryIslands(null);
    }
  }, [region]);

  // Verificación automática VIES cuando se selecciona UE y hay VAT y país
  useEffect(() => {
    // Solo verificar si:
    // - Es residente en UE
    // - No es persona física
    // - Hay VAT y país disponibles
    // - No se está verificando ya
    if (
      residencyType === 'eu' &&
      entityType !== 'individual' &&
      entityType !== '' &&
      vat &&
      country &&
      !isCheckingVIES
    ) {
      // Verificar si ya tenemos un resultado para este VAT/país
      const countryCode = country.toUpperCase();
      const cleanVat = vat.replace(/[\s-]/g, '').toUpperCase().replace(countryCode, '');
      const shouldCheck = !viesResult || viesResult.country_code !== countryCode || viesResult.vat_number !== cleanVat;

      if (shouldCheck) {
        checkVIES();
      }
    } else if (residencyType !== 'eu') {
      // Limpiar resultados si cambia la residencia
      setViesResult(null);
      setViesError(null);
      setHasVATRegistration(null);
    }
  }, [residencyType, entityType, vat, country, isCheckingVIES, viesResult, checkVIES]);

  // Calcular IVA basado en las respuestas
  useEffect(() => {
    if (!entityType || !residencyType) {
      setVatResult(null);
      if (lastCalculatedValue.current !== null) {
        lastCalculatedValue.current = null;
        onChange(''); // Resetear cuando no hay datos suficientes
      }
      return;
    }

    let result: VATResult;

    // Persona física (particular) - Siempre 21%
    if (entityType === 'individual') {
      result = {
        percentage: 21,
        subject: true,
        description: t('vatCalculator.results.individual'),
      };
    }
    // Profesional autónomo o Persona jurídica
    else if (entityType === 'self_employed' || entityType === 'legal_entity') {
      if (residencyType === 'spain') {
        // Residente en España
        if (isCanaryIslands === true) {
          result = {
            percentage: 0,
            subject: false,
            description: t('vatCalculator.results.canaryIslands'),
          };
        } else if (isCanaryIslands === false) {
          result = {
            percentage: 21,
            subject: true,
            description: t('vatCalculator.results.spainGeneral'),
          };
        } else {
          // Aún no se ha respondido sobre Canarias
          setVatResult(null);
          return;
        }
      } else if (residencyType === 'eu') {
        // Residente en otro país de la UE
        if (hasVATRegistration === true) {
          result = {
            percentage: 0,
            subject: false,
            description: t('vatCalculator.results.intraCommunity'),
            isIntraCommunity: true,
          };
        } else if (hasVATRegistration === false) {
          result = {
            percentage: 21,
            subject: true,
            description: t('vatCalculator.results.euNoRegistration'),
          };
        } else {
          // Aún no se ha respondido
          setVatResult(null);
          return;
        }
      } else {
        // Residente fuera de la UE
        result = {
          percentage: 0,
          subject: false,
          description: t('vatCalculator.results.outsideEU'),
        };
      }
    } else {
      setVatResult(null);
      return;
    }

    setVatResult(result);

    // Convertir el resultado a código de IVA y llamar onChange
    const taxCode = getTaxCode(result);
    if (lastCalculatedValue.current !== taxCode) {
      lastCalculatedValue.current = taxCode;
      onChange(taxCode);
    }
  }, [entityType, residencyType, isCanaryIslands, hasVATRegistration, t, onChange]);

  // Opciones para tipo de entidad
  const entityTypeOptions: RadioOption[] = [
    {
      value: 'individual',
      label: t('vatCalculator.entityTypes.individual'),
      description: t('vatCalculator.entityTypes.individualDescription'),
    },
    {
      value: 'self_employed',
      label: t('vatCalculator.entityTypes.selfEmployed'),
      description: t('vatCalculator.entityTypes.selfEmployedDescription'),
    },
    {
      value: 'legal_entity',
      label: t('vatCalculator.entityTypes.legalEntity'),
      description: t('vatCalculator.entityTypes.legalEntityDescription'),
    },
  ];

  // Opciones para residencia
  const residencyOptions: RadioOption[] = [
    {
      value: 'spain',
      label: t('vatCalculator.residencyTypes.spain'),
      description: t('vatCalculator.residencyTypes.spainDescription'),
    },
    {
      value: 'eu',
      label: t('vatCalculator.residencyTypes.eu'),
      description: t('vatCalculator.residencyTypes.euDescription'),
    },
    {
      value: 'outside_eu',
      label: t('vatCalculator.residencyTypes.outsideEU'),
      description: t('vatCalculator.residencyTypes.outsideEUDescription'),
    },
  ];

  // Opciones para Canarias
  const canaryOptions: RadioOption[] = [
    {
      value: 'yes',
      label: t('vatCalculator.canaryIslands.yes'),
      description: t('vatCalculator.canaryIslands.yesDescription'),
    },
    {
      value: 'no',
      label: t('vatCalculator.canaryIslands.no'),
      description: t('vatCalculator.canaryIslands.noDescription'),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Paso 1: Tipo de entidad */}
      <div className="flex flex-col gap-1">
        <Label className="pb-2">
          {t('vatCalculator.steps.entityType')} <span className="text-destructive">*</span>
        </Label>
        <RadioSelect
          options={entityTypeOptions}
          value={entityType}
          setValue={(val) => {
            setEntityType(val as EntityType);
            // Resetear pasos siguientes si cambia el tipo de entidad
            if (val !== entityType) {
              setResidencyType('');
              setIsCanaryIslands(null);
              setHasVATRegistration(null);
            }
          }}
          placeholder={t('vatCalculator.placeholders.selectEntityType')}
          dropdownWidth="lg"
          className="text-sm py-2.5"
        />
      </div>

      {/* Paso 2: Residencia (solo si se seleccionó tipo de entidad) */}
      {entityType && (
        <div className="flex flex-col gap-1">
          <Label className="pb-2">
            {t('vatCalculator.steps.residency')} <span className="text-destructive">*</span>
          </Label>
          <RadioSelect
            options={residencyOptions}
            value={residencyType}
            setValue={(val) => {
              setResidencyType(val as ResidencyType);
              // Resetear pasos siguientes si cambia la residencia
              if (val !== residencyType) {
                setIsCanaryIslands(null);
                setHasVATRegistration(null);
              }
            }}
            placeholder={t('vatCalculator.placeholders.selectResidency')}
            dropdownWidth="lg"
            className="text-sm py-2.5"
          />
        </div>
      )}

      {/* Paso 3a: Canarias (solo si es residente en España y no es persona física) */}
      {entityType && entityType !== 'individual' && residencyType === 'spain' && (
        <div className="flex flex-col gap-1">
          <Label className="pb-2">
            {t('vatCalculator.steps.canaryIslands')} <span className="text-destructive">*</span>
          </Label>
          <RadioSelect
            options={canaryOptions}
            value={isCanaryIslands === null ? '' : isCanaryIslands ? 'yes' : 'no'}
            setValue={(val) => setIsCanaryIslands(val === 'yes')}
            placeholder={t('vatCalculator.placeholders.selectCanaryIslands')}
            dropdownWidth="lg"
            className="text-sm py-2.5"
          />
        </div>
      )}

      {/* Paso 3b: Verificación automática VIES (solo si es residente en UE y no es persona física) */}
      {entityType && entityType !== 'individual' && residencyType === 'eu' && (
        <div className="flex flex-col gap-2">
          <Label className="pb-2">{t('vatCalculator.steps.vatRegistration')}</Label>

          {/* Estado de carga */}
          {isCheckingVIES && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-card-bg">
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">{t('vatCalculator.vies.checking')}</span>
            </div>
          )}

          {/* Resultado de verificación VIES */}
          {!isCheckingVIES && viesResult && (
            <div
              className={cn(
                'p-4 rounded-lg border-2 text-sm transition-colors',
                viesResult.valid ? 'border-success/50 bg-success/5' : 'border-destructive/50 bg-destructive/5',
              )}
            >
              {viesResult.valid ? (
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-success text-success-foreground">
                    {t('vatCalculator.vies.valid')}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {viesResult.country_code} {viesResult.vat_number}
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">{t('vatCalculator.vies.invalid')}</Badge>
                    {viesResult.vat_number && (
                      <span className="text-xs text-muted-foreground">
                        {viesResult.country_code} {viesResult.vat_number}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{t('vatCalculator.vies.invalidDescription')}</p>
                </div>
              )}
            </div>
          )}

          {/* Mensaje cuando falta VAT o país */}
          {!isCheckingVIES && !viesResult && !viesError && (!vat || !country) && (
            <div className="p-3 rounded-lg border border-border bg-card-bg">
              <p className="text-sm text-muted-foreground">{t('vatCalculator.vies.waitingForData')}</p>
            </div>
          )}

          {/* Error de verificación */}
          {!isCheckingVIES && viesError && (
            <div className="p-3 rounded-lg border border-destructive/50 bg-destructive/5">
              <p className="text-sm text-destructive mb-2">{viesError}</p>
              <a
                href="https://ec.europa.eu/taxation_customs/vies/#/vat-validation"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
              >
                {t('vatCalculator.vies.manualCheck')} →
              </a>
            </div>
          )}
        </div>
      )}

      {/* Resultado del cálculo */}
      {vatResult && (
        <div
          className={cn(
            'mt-4 p-3 rounded-lg border-2 transition-colors',
            vatResult.subject ? 'border-primary/50 bg-primary/5' : 'border-border bg-card-bg',
          )}
        >
          <div className="flex items-center gap-3">
            <Badge
              variant={vatResult.subject ? 'default' : 'outline'}
              className={cn(vatResult.subject ? 'bg-primary text-white text-sm px-3 py-1' : 'text-sm px-3 py-1')}
            >
              IVA {vatResult.percentage}%
            </Badge>
            <span className="text-sm text-foreground">{vatResult.description}</span>
          </div>
        </div>
      )}
    </div>
  );
};
